import { Geogroup, Place, PlaceIcon, UserPlace } from '@geovelo-frontends/commons';
import {
  Autocomplete,
  Box,
  BoxProps,
  Collapse,
  FilterOptionsState,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import distance from '@turf/distance';
import { Marker } from 'maplibre-gl';
import {
  forwardRef,
  Ref,
  RefObject,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { AppContext } from '../../app/context';
import usePartnerSubgroupsQuery from '../../hooks/use-partner-subgroups-query';

import PlaceForm from './form';

export const noCorrespondingSiteItem = {
  id: 'noCorrespondingSite',
  titleKey: 'companies.pages.admin.user.teams_form.no_match',
} as const;

type TOption = Geogroup | typeof noCorrespondingSiteItem;

export type TSelectedSite = Geogroup | typeof noCorrespondingSiteItem | null;

export type TWorkFormRef = {
  setSearch: (search: string) => void;
};

function WorkForm(
  {
    initialized,
    mapId,
    markerRef,
    size,
    disableLabel,
    disableSites,
    isSubmitting,
    initialPlace,
    sites: customSites,
    home: customHome,
    selectedSite,
    disableNearHome,
    enableSuggestions,
    disableNoCorrespondingSite,
    updatedPlace,
    selectSite,
    setUpdated,
    onPlaceUpdated,
  }: {
    disableLabel?: boolean;
    disableNoCorrespondingSite?: boolean;
    disableNearHome?: boolean;
    disableSites?: boolean;
    enableSuggestions?: boolean;
    home?: UserPlace | Place | null;
    isSubmitting?: boolean;
    initialized: boolean;
    initialPlace?: UserPlace | Place | null;
    mapId?: string;
    markerRef?: RefObject<Marker | null>;
    onPlaceUpdated?: (place: Place | null) => void;
    selectedSite: TSelectedSite;
    selectSite: (site: TSelectedSite) => void;
    setUpdated?: (updated: boolean) => void;
    sites?: Geogroup[] | null;
    size?: 'small';
    updatedPlace?: UserPlace | Place | null;
  },
  ref: Ref<TWorkFormRef>,
): React.JSX.Element {
  const [search, setSearch] = useState('');
  const [sites, setSites] = useState<Geogroup[]>();
  const {
    partner: { current: currentPartner },
    user: { home: userHome },
  } = useContext(AppContext);
  const { data: partnerSubgroups } = usePartnerSubgroupsQuery(currentPartner);
  const partnerSites = partnerSubgroups?.sites;
  const { t } = useTranslation();

  useImperativeHandle(ref, () => ({
    setSearch,
  }));

  useEffect(() => {
    const _sites = customSites || partnerSites;
    const home = customHome || userHome;

    setSites(
      _sites?.sort((a, b) => {
        if (!disableNearHome && home) {
          if (a.place && b.place)
            return distance(a.place.point, home.point) - distance(b.place.point, home.point);
          if (a.place) return -1;
          if (b.place) return 1;
        }

        return b.nbMembers - a.nbMembers;
      }) || [],
    );
  }, [userHome, partnerSites]);

  const noCorrespondingSite =
    (selectedSite && selectedSite.id === 'noCorrespondingSite') ||
    (sites && sites.length === 0) ||
    false;

  function filterOptions(options: TOption[], state: FilterOptionsState<TOption>) {
    const filteredOptions = createFilterOptions<TOption>({
      limit: state.inputValue || !disableNearHome || enableSuggestions ? undefined : 0,
      ignoreAccents: true,
    })(options, state);

    if (!disableNoCorrespondingSite && (state.inputValue || options.length < 3))
      filteredOptions.push(noCorrespondingSiteItem);

    return filteredOptions;
  }

  const home = customHome || userHome;

  return (
    <Box display="flex" flexDirection="column" width="100%">
      {!disableSites && sites && sites.length > 0 && (
        <Box display="flex" flexDirection="column" gap={0.5}>
          {!disableLabel && (
            <Typography fontWeight={700} variant="body2">
              {t('companies.pages.admin.user.teams_form.labels_site')}
            </Typography>
          )}
          <Autocomplete
            freeSolo
            disabled={isSubmitting}
            filterOptions={filterOptions}
            forcePopupIcon={false}
            getOptionKey={(option) => (typeof option !== 'string' ? option.id : option)}
            getOptionLabel={(option) =>
              typeof option !== 'string'
                ? 'titleKey' in option
                  ? t(option.titleKey)
                  : option.title
                : option
            }
            groupBy={() => ''}
            id="sites-autocomplete"
            inputValue={search}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={
              <Typography variant="body2">
                {t('companies.pages.admin.user.teams_form.no_option_site')}
              </Typography>
            }
            onChange={(_, value) => {
              if (typeof value !== 'string') {
                onPlaceUpdated?.(!value || value.id === 'noCorrespondingSite' ? null : value.place);
                setUpdated?.(true);
                selectSite(value);
              }
            }}
            onInputChange={(_, value) => setSearch(value)}
            options={sites}
            renderGroup={({ key, children }) => (
              <li key={key}>
                {!disableNearHome && !search && home && (
                  <Box alignItems="center" display="flex" height={32} paddingX={3}>
                    <Typography color="textSecondary" variant="body2">
                      {t('companies.pages.admin.user.teams_form.near_home')}
                    </Typography>
                  </Box>
                )}
                <ul style={{ padding: 0 }}>{children}</ul>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                autoComplete="off"
                margin="none"
                placeholder={t('companies.pages.admin.user.teams_form.titles_site')}
                size="small"
                slotProps={{
                  input: {
                    ...params.InputProps,
                    sx: size === 'small' ? { fontSize: '0.875rem' } : {},
                  },
                  inputLabel: { ...params.InputLabelProps, shrink: true },
                }}
              />
            )}
            renderOption={({ key, ...itemProps }, option) => {
              if (option.id === 'noCorrespondingSite') {
                return (
                  <Box
                    key={key}
                    {...(itemProps as unknown as BoxProps)}
                    alignItems="center"
                    display="flex"
                    height={52}
                  >
                    <Typography color="primary" marginX={1} variant="body2">
                      {t(option.titleKey)}
                    </Typography>
                  </Box>
                );
              }

              return (
                <Box
                  alignItems="center"
                  display="flex"
                  gap={2}
                  key={key}
                  {...(itemProps as unknown as BoxProps)}
                >
                  {size !== 'small' && <PlaceIcon color="primary" />}
                  <Box display="flex" flexDirection="column">
                    <Typography variant="body2">{option.title}</Typography>
                    {option.place?.addressDetail && size !== 'small' && (
                      <Typography color="textSecondary" variant="caption">
                        {option.place.addressDetail.secondaryText}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
            value={selectedSite}
          />
        </Box>
      )}
      {!disableNoCorrespondingSite && mapId && markerRef && (
        <Collapse in={noCorrespondingSite}>
          <Box marginTop={2}>
            <PlaceForm
              disableLabel={disableSites}
              initialized={initialized}
              initialPlace={initialPlace}
              isSubmitting={isSubmitting}
              mapId={mapId}
              markerRef={markerRef}
              onPlaceUpdated={onPlaceUpdated}
              placeType="work"
              setUpdated={setUpdated}
              updatedPlace={updatedPlace}
            />
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

export default forwardRef(WorkForm);
